import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { QuickStartGuide } from '../components/QuickStartGuide';
import type { Application } from '../lib/api';
import { useAuth } from '../lib/useAuth';
import { useHomeData } from '../lib/useHomeData';
import { useQuickStartGuide } from '../lib/useQuickStartGuide';
import { useWelcomeBackPoll } from '../lib/useWelcomeBackPoll';
import { countNewSinceLastVisit, resolveWelcomeBackGreeting } from '../lib/welcomeBack';
import { ThemeSwatches } from '../theme/ThemeSwatches';
import styles from './Home.module.css';

/**
 * Home tab — Wave-1 C1 REAL-DATA rework (docs/DEMO-WALKTHROUGH-issues.md #1/
 * #2/#3). Previously rendered placeholderHome/placeholderJobs UNCONDITIONALLY
 * for every signed-in user, including a brand-new 0-corpus account — DB-
 * verified proof: a net-new user (0 roles/0 jobs/0 apps) landed here showing
 * "62% told · 12 roles read · 68 found score" and three invented job rows
 * (the exact fabricated company names are recorded in issue #1 itself) as if
 * they were the user's own. That is exactly the invention the NO-INVENTION
 * law forbids (CLAUDE.md's foundational law): every number/name/job on this
 * screen must now trace to a real, served value, or the screen shows nothing
 * for that slot — never a fabricated placeholder shape. (This file's own
 * noFabricatedPlaceholders.guard.test.ts mechanically proves those exact
 * strings never appear here again — this doc comment deliberately does not
 * repeat them, so the guard's string list stays the ONE place they exist in
 * source.)
 *
 * Every value below comes from useHomeData (real GET /corpus/roles, GET
 * /corpus/readiness, GET /applications, GET /presence/target-roles, GET
 * /jobs) + useAuth (the real signed-in user's displayName — fixes #2, "Good
 * morning there" never resolving to Jason's real name). placeholderData.ts
 * is no longer imported by this file at all.
 *
 * COLD STATE (roleCount === 0, honest empty, not a blank screen): a warm
 * "let's start your story" card pointing into Refine/onboarding — never the
 * placeholder's invented 62%/12-roles/68-score/three-jobs shape. This mirrors
 * CorpusCanvas.tsx's/Jobs.tsx's own "say only what is actually true" empty-
 * state law, applied here for the first time.
 *
 * LANE U1 — Jason's direct spec, pieces (b)/(c):
 *   (b) the dismissible quick-start guide (QuickStartGuide.tsx,
 *       useQuickStartGuide) — shown here for a signed-in user who hasn't
 *       dismissed it yet, real step-done checks over the SAME useHomeData
 *       fields this file already reads (roleCount/targetRoleCount/
 *       applications.length), never a fabricated progress state.
 *   (c) the state-aware "Welcome back" greeting (welcomeBack.ts's
 *       resolveWelcomeBackGreeting) + its CHECK NOW affordance
 *       (useWelcomeBackPoll, the SAME POST /watchlist/poll call Jobs' "Check
 *       for new jobs" button uses) + the honest new-since-last-visit count
 *       (welcomeBack.ts's countNewSinceLastVisit, tallied over the SAME GET
 *       /jobs rows useHomeData now also reads — see that hook's own doc
 *       comment for why this is a normal DB read, not the heavier poll).
 */
export function Home() {
  const auth = useAuth();
  const data = useHomeData();
  const quickStart = useQuickStartGuide();
  const welcomeBackPoll = useWelcomeBackPoll();

  const displayName = auth.user?.displayName?.trim() || null;
  const initial = (displayName ?? auth.user?.email ?? '?')[0]?.toUpperCase() ?? '?';

  // Cold start: a real, server-confirmed 0-role corpus (never a mid-load
  // guess — roleCount stays null, not 0, while the read is in flight or
  // failed, so this branch only fires once GET /corpus/roles has genuinely
  // confirmed zero).
  const isColdStart = !data.loading && data.roleCount === 0;

  const greeting = resolveWelcomeBackGreeting({
    roleCount: data.roleCount,
    quickStartDismissed: quickStart.dismissed,
  });
  const newSinceLastVisit = countNewSinceLastVisit(data.jobs);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.avatar}>{initial}</div>
          <div>
            <div className={styles.greeting}>{greeting.eyebrow}</div>
            {/* #2 (Opus sweep m2): no displayName on record → the name line is
             *  DROPPED, never a "there"/"friend" stand-in — the QA fix as
             *  recorded (a generic token isn't invention, but the recorded fix
             *  is the drop, and it reads more human). */}
            {displayName ? <div className={styles.greetingName}>{displayName}</div> : null}
          </div>
        </div>
        <div className={styles.headerRight}>
          <ThemeSwatches />
          <Link to="/settings" className={styles.settingsButton} aria-label="Settings">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M10.3 3.2a1.7 1.7 0 0 1 3.4 0l.2 1.1a1.7 1.7 0 0 0 2.4 1.2l1-.5a1.7 1.7 0 0 1 2.1.7l.9 1.6a1.7 1.7 0 0 1-.4 2.2l-.9.7a1.7 1.7 0 0 0 0 2.4l.9.7a1.7 1.7 0 0 1 .4 2.2l-.9 1.6a1.7 1.7 0 0 1-2.1.7l-1-.5a1.7 1.7 0 0 0-2.4 1.2l-.2 1.1a1.7 1.7 0 0 1-3.4 0l-.2-1.1a1.7 1.7 0 0 0-2.4-1.2l-1 .5a1.7 1.7 0 0 1-2.1-.7l-.9-1.6a1.7 1.7 0 0 1 .4-2.2l.9-.7a1.7 1.7 0 0 0 0-2.4l-.9-.7a1.7 1.7 0 0 1-.4-2.2l.9-1.6a1.7 1.7 0 0 1 2.1-.7l1 .5a1.7 1.7 0 0 0 2.4-1.2l.2-1.1Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </Link>
        </div>
      </header>

      {greeting.showCheckNow ? (
        <WelcomeBackBanner
          subline={greeting.subline}
          newCount={newSinceLastVisit}
          poll={welcomeBackPoll.poll}
          onCheckNow={welcomeBackPoll.runPollNow}
        />
      ) : null}

      {quickStart.visible ? (
        <QuickStartGuide
          state={{
            roleCount: data.roleCount,
            targetRoleCount: data.targetRoleCount,
            applicationCount: data.applications.length,
          }}
          onDismiss={quickStart.dismiss}
        />
      ) : null}

      {data.loading ? (
        <div className={styles.status}>Loading your story…</div>
      ) : isColdStart ? (
        <ColdStartHome />
      ) : (
        <RealHome
          roleCount={data.roleCount}
          storyPct={data.readiness?.score ?? null}
          goldCount={data.readiness?.parts.gold ?? null}
          applications={data.applications}
        />
      )}
    </div>
  );
}

interface WelcomeBackBannerProps {
  subline: string | null;
  /** welcomeBack.ts's countNewSinceLastVisit over the SAME GET /jobs rows
   *  useHomeData reads — a real tally, never a server rollup or an invented
   *  figure (see welcomeBack.ts's own doc comment). */
  newCount: number;
  poll: ReturnType<typeof useWelcomeBackPoll>['poll'];
  onCheckNow: () => void;
}

/**
 * The returning-user banner (LANE U1, piece (c)). Tap-only by design — the
 * brief: "never auto-fire the poll on mount... prefer the tap." CHECK NOW
 * hits the SAME POST /watchlist/poll useJobsInbox's runPollNow already calls
 * (useWelcomeBackPoll wraps pollWatchlist() directly), and renders the
 * server's own honest PollSummary once done — mirrors JobSearchBar.tsx's
 * describePollSummary discipline: only real served counts, never a guess.
 */
function WelcomeBackBanner({ subline, newCount, poll, onCheckNow }: WelcomeBackBannerProps) {
  const polling = poll.phase === 'polling';

  return (
    <Card className={styles.welcomeBackCard}>
      <div className={styles.welcomeBackRow}>
        <div className={styles.welcomeBackBody}>
          <div className={styles.welcomeBackSubline}>{subline}</div>
          {newCount > 0 ? (
            // His 2026-07-21 walk: the count was a dead announcement — it now
            // links straight to the jobs board (the New chip sits right at the
            // top there). Bucket-preselect via URL is the registered follow-up.
            <Link to="/jobs" className={styles.welcomeBackCount}>
              {newCount} new {newCount === 1 ? 'role' : 'roles'} waiting for you — see them
            </Link>
          ) : null}
        </div>
        <button type="button" className={styles.checkNowButton} onClick={onCheckNow} disabled={polling}>
          {polling ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Checking…
            </>
          ) : (
            'Check now'
          )}
        </button>
      </div>
      {poll.phase === 'done' && poll.summary ? (
        <div className={styles.welcomeBackResult} role="status">
          {describeWelcomeBackPoll(poll.summary)}
        </div>
      ) : null}
      {poll.phase === 'error' && poll.error ? (
        <div className={styles.welcomeBackError} role="alert">
          Couldn't check right now: {poll.error}
        </div>
      ) : null}
    </Card>
  );
}

/** Honest, served-count-only summary of a completed poll — mirrors
 *  JobSearchBar.tsx's describePollSummary exactly (same fields, same "no
 *  invented counts" discipline), kept as its own small function here since
 *  that one is a module-private helper in a different file. */
function describeWelcomeBackPoll(summary: NonNullable<ReturnType<typeof useWelcomeBackPoll>['poll']['summary']>): string {
  if (summary.ingestedNew > 0) {
    return `Found ${summary.ingestedNew} new job${summary.ingestedNew === 1 ? '' : 's'}.`;
  }
  if (summary.sourcesPolled > 0) {
    return `Checked ${summary.sourcesPolled} source${summary.sourcesPolled === 1 ? '' : 's'} — nothing new.`;
  }
  return 'No sources to check yet.';
}

/**
 * The honest cold-start Home (#1/#51's fix, presentation half): a real
 * 0-corpus user gets a truthful "let's start your story" card, never the
 * placeholder's fabricated 62%/12-roles/68-score/three-jobs shape. Points
 * into Refine (where the real voice/paste capture lives) — the same
 * hand-off Onboarding.tsx's own steps already use, so this reads as a
 * continuation of the same flow rather than a dead end.
 */
function ColdStartHome() {
  return (
    <>
      <Card className={styles.storyCard} tourAnchor="home-story-score">
        <div className={styles.storyLabel}>Your story so far</div>
        <div className={styles.storyPct}>Not started yet</div>
        <div className={styles.storyNote}>
          Nothing here is invented — once you tell me even one story, this fills in with what's
          actually true about you.
        </div>
      </Card>

      <Link to="/refine" className={styles.continueCard}>
        <div className={styles.continueIcon}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M21 12a8 8 0 0 1-11.5 7.2L4 20.5l1.3-5.2A8 8 0 1 1 21 12Z"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className={styles.continueBody}>
          <div className={styles.continueTitle}>Let's start your story</div>
          <div className={styles.continueSub}>Tell me one thing you're proud of — that's all it takes</div>
        </div>
        <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
          <path
            d="M1 1l6 7-6 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Link>

      <div className={styles.encourage}>
        <span className={styles.encourageIcon}>🌱</span>
        <div className={styles.encourageText}>
          You don't have to sell yourself today. Just tell the truth — I'll find the words.
        </div>
      </div>
    </>
  );
}

interface RealHomeProps {
  roleCount: number | null;
  storyPct: number | null;
  goldCount: number | null;
  applications: Application[];
}

/**
 * The data-rich Home: every stat is independently null-safe (a slow/failed
 * read on ONE signal never blanks the others — see useHomeData's own doc
 * comment) and "Jobs you're working on" renders REAL tracked applications,
 * never the placeholder's invented job rows (see this file's own top-of-file
 * doc comment for why the exact fabricated names are deliberately not
 * repeated here). An application row carries no fit score of its own (GET
 * /applications does not join job_scores — that join is GET /jobs' job, see
 * api.ts's own CASING WARNING) so this list shows title/company honestly,
 * without a match ring it cannot back with a real number.
 */
function RealHome({ roleCount, storyPct, goldCount, applications }: RealHomeProps) {
  const topApplications = applications.slice(0, 3);

  return (
    <>
      <Card className={styles.storyCard} tourAnchor="home-story-score">
        <div className={styles.storyLabel}>Your story so far</div>
        {storyPct !== null ? (
          <>
            <div className={styles.storyPct}>{storyPct}% told</div>
            <div className={styles.storyNote}>
              {goldCount !== null && goldCount > 0
                ? `I've got ${goldCount} gold ${goldCount === 1 ? 'moment' : 'moments'} banked from your work.`
                : "Keep talking to me — the more I hear, the more this fills in."}
            </div>
            <div className={styles.storyTrack}>
              <div className={styles.storyBar} style={{ width: `${storyPct}%` }} />
            </div>
          </>
        ) : (
          <div className={styles.storyNote}>Still reading your story — check back in a moment.</div>
        )}
      </Card>

      <Link to="/refine" className={styles.continueCard}>
        <div className={styles.continueIcon}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M21 12a8 8 0 0 1-11.5 7.2L4 20.5l1.3-5.2A8 8 0 1 1 21 12Z"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className={styles.continueBody}>
          <div className={styles.continueTitle}>Pick up with Meira</div>
          <div className={styles.continueSub}>Keep building out your story</div>
        </div>
        <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true">
          <path
            d="M1 1l6 7-6 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Link>

      <div className={styles.statTrio}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{roleCount ?? '—'}</div>
          <div className={styles.statLabel}>roles read</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{goldCount ?? '—'}</div>
          <div className={styles.statLabel}>gold moments</div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statValue} ${styles.statValueSupport}`}>
            {storyPct !== null ? storyPct : '—'}
          </div>
          <div className={styles.statLabel}>story score</div>
        </div>
      </div>

      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>Jobs you're working on</div>
        <Link to="/track" className={styles.seeAll}>
          See all
        </Link>
      </div>
      {topApplications.length > 0 ? (
        <div className={styles.jobList}>
          {topApplications.map((app) => (
            <Link key={app.id} to={`/jobs/${app.jobId}`} className={styles.jobRow}>
              <div className={styles.jobLogo}>{(app.jobCompany ?? '?')[0]?.toUpperCase() ?? '?'}</div>
              <div className={styles.jobBody}>
                <div className={styles.jobTitle}>{app.jobTitle ?? 'Untitled role'}</div>
                <div className={styles.jobMeta}>{app.jobCompany ?? 'Unknown company'}</div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className={styles.emptyJobsNote}>
          Nothing tracked yet — hop over to Jobs and find something worth going after.
        </div>
      )}

      <div className={styles.encourage}>
        <span className={styles.encourageIcon}>🌱</span>
        <div className={styles.encourageText}>
          You don't have to sell yourself today. Just tell the truth — I'll find the words.
        </div>
      </div>
    </>
  );
}
